import { useState } from 'react';
import { css } from '@emotion/react';
import { PhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { Modal, Text, Button, useAlert } from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { PhoneNumbersTable } from './phone-numbers-table';
import { ExtendedCallRoute } from './types';

export const EditPhoneNumbersModal = ({
  callRoute,
  tenantLocation,
  unallocatedPhoneNumbers,
  isDeviceExtensionRoute = false,
  onClose,
}: {
  callRoute: ExtendedCallRoute;
  unallocatedPhoneNumbers: PhoneNumber[];
  tenantLocation: SettingsTenantLocation;
  isDeviceExtensionRoute?: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const [selectedPhoneNumberIds, setSelectedPhoneNumberIds] = useState<string[]>(callRoute.phoneNumberIds);

  const { mutate: updateCallRoutePhoneNumbers, isLoading: updateCallRoutePhoneNumbersIsLoading } =
    CallRouteV1.Mutations.useUpdatePhoneNumbersMutation({
      options: {
        onSuccess: () => {
          alerts.success(t('Phone Numbers updated successfully.'));
          onClose();
        },
        onError: () => {
          alerts.error(t('Failed to update phone numbers.'));
        },
      },
    });

  const handleSave = () => {
    updateCallRoutePhoneNumbers({
      callRouteId: callRoute.callRouteId,
      phoneNumberIds: selectedPhoneNumberIds,
    });
  };

  return (
    <>
      <Modal.Header onClose={onClose}>{t('Assign Phone Numbers')}</Modal.Header>
      <Modal.Body>
        <Text color='light'>{callRoute.name}</Text>
        <Text>
          {isDeviceExtensionRoute
            ? t('Assign phone numbers to this Device Route so it can be called.')
            : t('Assign phone numbers to this Call Route so it can be called.')}
        </Text>

        <PhoneNumbersTable
          tenantLocation={tenantLocation}
          phoneNumbers={[...callRoute.phoneNumbers, ...unallocatedPhoneNumbers]}
          selectedPhoneNumberIds={selectedPhoneNumberIds}
          setSelectedPhoneNumberIds={setSelectedPhoneNumberIds}
          trackingIdBase={trackingId({
            context: 'setting',
            feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-routes',
            details: 'edit-phone-numbers',
          })}
        />
      </Modal.Body>
      <Modal.Footer
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        {selectedPhoneNumberIds.length > 0 && (
          <Text color='light' textAlign='right'>
            {selectedPhoneNumberIds.length > 1
              ? t('{{count}} phone numbers selected', { count: selectedPhoneNumberIds.length })
              : t('{{count}} phone number selected', { count: selectedPhoneNumberIds.length })}
          </Text>
        )}
        <Button
          variant='secondary'
          size='large'
          onClick={onClose}
          trackingId={trackingId({
            context: 'setting',
            feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-routes',
            details: 'edit-phone-numbers-cancel-btn',
          })}
          css={css`
            width: fit-content;
          `}
        >
          {t('Cancel')}
        </Button>
        <Button
          size='large'
          disabled={updateCallRoutePhoneNumbersIsLoading}
          onClick={handleSave}
          trackingId={trackingId({
            context: 'setting',
            feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-routes',
            details: 'edit-phone-numbers-save-btn',
          })}
          css={css`
            width: fit-content;
          `}
        >
          {t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};
